"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdventureScene = void 0;
class AdventureScene {
    constructor(data) {
        this.id = data.id;
        this.style = data.style;
        this.autoEffect = data.autoEffect;
        this.locationTag = data.locationTag;
        this.sections = [...data.sections]; // Create a copy to prevent mutation
        this.variables = data.variables ? [...data.variables] : undefined; // Create a copy to prevent mutation
    }
    getAllOptions() {
        return this.sections.flatMap((_, index) => this.getSectionOptions(index));
    }
    getOption(index) {
        const allOptions = this.getAllOptions();
        return allOptions[index];
    }
    getOptionCount() {
        return this.getAllOptions().length;
    }
    hasOptions() {
        return this.getAllOptions().length > 0;
    }
    hasAutoEffects() {
        return !!this.autoEffect;
    }
    getAllDescriptions() {
        return this.sections
            .map(section => section.description)
            .filter((desc) => desc !== undefined);
    }
    getPlainTextDescriptions() {
        return this.getAllDescriptions().map(description => {
            if (typeof description === 'string') {
                return description;
            }
            // Convert rich text to plain text for backward compatibility
            return description.elements
                .map(element => this.elementToPlainText(element))
                .join('\n\n');
        });
    }
    getFinishOptions() {
        return this.getAllOptions().filter(option => option.target === 'FINISH');
    }
    hasFinishOption() {
        return this.getFinishOptions().length > 0;
    }
    getLeaveOptions() {
        return this.getAllOptions().filter(option => option.target === 'LEAVE');
    }
    hasLeaveOption() {
        return this.getLeaveOptions().length > 0;
    }
    /** True if any option (including inline-derived) can invoke RETURN_TO_ADVENTURE (literal or script target). */
    hasReturnToAdventureOption() {
        return this.getAllOptions().some(option => {
            const t = option.target;
            if (typeof t !== 'string' || !t.trim())
                return false;
            if (t.trim() === 'RETURN_TO_ADVENTURE')
                return true;
            if (t.includes("'''") && t.includes('RETURN_TO_ADVENTURE'))
                return true;
            const isScriptExpression = t.includes('+') || t.includes('getVariable') || t.includes('getAdventureInvite');
            return isScriptExpression && t.includes('RETURN_TO_ADVENTURE');
        });
    }
    getSectionOptions(sectionIndex) {
        const section = this.sections[sectionIndex];
        if (!section)
            return [];
        return [
            ...section.options,
            ...this.getInlineOptionsForSection(sectionIndex)
        ];
    }
    getInlineOptionsForSection(sectionIndex) {
        const section = this.sections[sectionIndex];
        if (!section || !section.description || typeof section.description !== 'object') {
            return [];
        }
        return this.extractInlineOptions(section.description.elements);
    }
    findOptionById(optionId) {
        return this.getAllOptions().find(option => option.id === optionId);
    }
    getBackOptionCandidates() {
        return this.getAllOptions().filter(option => option.isBackOption);
    }
    getBackOptionId() {
        const candidates = this.getBackOptionCandidates();
        if (candidates.length === 1) {
            return candidates[0].id;
        }
        return undefined;
    }
    getInlineOptions() {
        return this.sections.flatMap((_, index) => this.getInlineOptionsForSection(index));
    }
    extractInlineOptions(elements) {
        const inlineOptions = [];
        for (const element of elements) {
            if (element.type === 'inline-option' && element.optionId && element.target) {
                inlineOptions.push({
                    id: element.optionId,
                    text: this.renderInlineOptionText(Array.isArray(element.content) ? element.content : []),
                    target: element.target,
                    effect: element.effects,
                    ...(element.condition ? { condition: element.condition } : {}),
                    ...(element.isBackOption ? { isBackOption: true } : {})
                });
            }
            if (element.type === 'conditional') {
                if (Array.isArray(element.children)) {
                    inlineOptions.push(...this.extractInlineOptions(element.children));
                }
                if (Array.isArray(element.elseChildren)) {
                    inlineOptions.push(...this.extractInlineOptions(element.elseChildren));
                }
            }
            else if (Array.isArray(element.content)) {
                inlineOptions.push(...this.extractInlineOptions(element.content));
            }
        }
        return inlineOptions;
    }
    renderInlineOptionText(content) {
        return content.map(element => {
            if (element.type === 'text') {
                return element.content;
            }
            else if (element.type === 'bold') {
                return `**${element.content}**`;
            }
            else if (element.type === 'italic') {
                return `*${element.content}*`;
            }
            else if (element.type === 'image') {
                // For images, use a placeholder text
                return `[${element.imageType}]`;
            }
            else if (Array.isArray(element.content)) {
                return this.renderInlineOptionText(element.content);
            }
            return '';
        }).join('');
    }
    elementToPlainText(element) {
        if (typeof element === 'string') {
            return element;
        }
        if (element.type === 'paragraph' && Array.isArray(element.content)) {
            return element.content.map((item) => this.elementToPlainText(item)).join('');
        }
        if (element.type === 'heading') {
            const prefix = '#'.repeat(element.level || 1) + ' ';
            return prefix + this.elementToPlainText(element.content);
        }
        if (element.type === 'list' && Array.isArray(element.content)) {
            return element.content
                .map((item, index) => {
                const prefix = element.listType === 'numbered' ? `${index + 1}. ` : '- ';
                return prefix + this.elementToPlainText(item);
            })
                .join('\n');
        }
        if (typeof element.content === 'string') {
            return element.content;
        }
        if (Array.isArray(element.content)) {
            return element.content.map((item) => this.elementToPlainText(item)).join('');
        }
        return '';
    }
    /**
     * Check if a description contains a sync option script (getCharacterSynchronisationWaitOption).
     * Used to allow scenes whose only interactive content is a sync option to pass validation.
     */
    hasSyncOptionInDescription(description) {
        if (!description)
            return false;
        if (typeof description === 'string') {
            return description.includes('getCharacterSynchronisationWaitOption');
        }
        return this.containsSyncOptionScript(description.elements ?? []);
    }
    containsSyncOptionScript(elements) {
        for (const element of elements) {
            if (element.type === 'sync-option')
                return true;
            if (element.type === 'script' && typeof element.scriptCode === 'string') {
                if (element.scriptCode.includes('getCharacterSynchronisationWaitOption')) {
                    return true;
                }
            }
            if (Array.isArray(element.content)) {
                if (this.containsSyncOptionScript(element.content)) {
                    return true;
                }
            }
            const withChildren = element;
            if (Array.isArray(withChildren.children) && this.containsSyncOptionScript(withChildren.children)) {
                return true;
            }
            if (Array.isArray(withChildren.elseChildren) && this.containsSyncOptionScript(withChildren.elseChildren)) {
                return true;
            }
        }
        return false;
    }
    validateOptions() {
        const errors = [];
        // Check that at least one section has options
        if (this.sections.length === 0) {
            errors.push(`Scene '${this.id}' must have at least one section`);
            return errors;
        }
        // Check for empty sections - allow sections with no options (for ending scenes)
        // Only validate that the scene has at least one section with options.
        // Scenes with description-based interactives (e.g. sync option script) also pass.
        const hasOptions = this.sections.some(section => section.options.length > 0);
        const hasDescriptionInteractives = this.sections.some(s => s.description && this.hasSyncOptionInDescription(s.description));
        if (!hasOptions && !hasDescriptionInteractives) {
            errors.push(`Scene '${this.id}' has no sections with options`);
        }
        // Check for empty options
        this.sections.forEach((section, sectionIndex) => {
            section.options.forEach((option, optionIndex) => {
                // Check if text is empty (handle both string and RichTextDescription)
                const isEmpty = !option.text ||
                    (typeof option.text === 'string' && option.text.trim() === '') ||
                    (typeof option.text === 'object' && option.text.elements.length === 0);
                if (isEmpty) {
                    errors.push(`Option ${optionIndex + 1} in section ${sectionIndex + 1} of scene '${this.id}' has empty text`);
                }
                if (!option.target || option.target.trim() === '') {
                    errors.push(`Option ${optionIndex + 1} in section ${sectionIndex + 1} of scene '${this.id}' has empty target`);
                }
            });
        });
        // Check for duplicate option texts across all sections (excluding inline options)
        const allOptions = this.getAllOptions();
        const regularOptions = allOptions.filter(opt => !opt.id.includes('_inline_'));
        const optionTexts = regularOptions.map(opt => opt.text);
        const duplicateTexts = optionTexts.filter((text, index) => optionTexts.indexOf(text) !== index);
        if (duplicateTexts.length > 0) {
            errors.push(`Scene '${this.id}' has duplicate option texts: ${duplicateTexts.join(', ')}`);
        }
        const backOptions = this.getAllOptions().filter(option => option.isBackOption);
        if (backOptions.length > 1) {
            errors.push(`Scene '${this.id}' has more than one back option`);
        }
        // Note: FinishOption validation is handled at the adventure level, not per scene
        return errors;
    }
    toJSON() {
        return {
            id: this.id,
            style: this.style,
            autoEffect: this.autoEffect,
            locationTag: this.locationTag,
            sections: [...this.sections]
        };
    }
}
exports.AdventureScene = AdventureScene;
//# sourceMappingURL=AdventureScene.js.map